import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { track } from "@/lib/api";
import { money } from "@/lib/pathways";
import { ArrowUpRight } from "lucide-react";
import { EmailCapture } from "@/components/EmailCapture";
import { CheaperWayCTA } from "@/components/CheaperWayCTA";

const CATS = ["All", "Technology", "Food", "Money", "Work", "Entertainment", "Home", "Shopping", "Travel", "Beauty", "Health"];

export default function Opportunities() {
  const [params, setParams] = useSearchParams();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const cat = params.get("category") || "All";
  const typeFilter = params.get("type");

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (cat !== "All") p.category = cat;
    if (typeFilter) p.type = typeFilter;
    api.get("/opportunities", { params: p })
      .then((r) => setOpps(r.data.opportunities || []))
      .finally(() => setLoading(false));
  }, [cat, typeFilter]);

  const setCat = (c) => {
    const next = {};
    if (c !== "All") next.category = c;
    if (typeFilter) next.type = typeFilter;
    setParams(next);
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="eyebrow mb-4">THE OPPORTUNITY ENGINE</div>
        <h1 className="font-display text-4xl md:text-6xl leading-[0.95] mb-3">WORTH KNOWING BEFORE YOU SPEND.</h1>
        <p className="font-serif text-lg text-stone-400 max-w-xl mb-3">Deals, free stuff, secondhand, refurbished, cashback, benefits, settlements and local finds — verified and dated. Expired ones never show.</p>
        {typeFilter && (
          <div className="mb-6"><span className="font-label text-xs tracking-widest text-[#EF3340] border border-[#EF3340] px-3 py-1">STRATEGY: {typeFilter}</span>
            <button onClick={() => setParams(cat !== "All" ? { category: cat } : {})} className="ml-3 font-label text-[10px] tracking-widest text-stone-500">CLEAR</button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-8">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`opp-cat-${c.toLowerCase()}`}
              className={`font-label text-xs tracking-widest px-4 py-2 border transition-colors ${cat === c ? "bg-[#EF3340] text-white border-[#EF3340]" : "hairline text-stone-400 hover:text-[#EF3340]"}`}>
              {c.toUpperCase()}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="font-display text-2xl text-stone-500">Loading…</p>
        ) : opps.length === 0 ? (
          <div className="max-w-2xl">
            <div className="eyebrow mb-3 text-[#EF3340]">NOT HERE YET.</div>
            <p className="font-serif text-xl text-stone-300 mb-6">We couldn't find anything live here yet. Know the cheaper way?</p>
            <CheaperWayCTA category={cat !== "All" ? cat : undefined} />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {opps.map((o, i) => (
              <a key={o.id} href={o.affiliate_url || o.destination_url || o.official_url || "#"} target="_blank" rel="noopener noreferrer"
                onClick={() => track("opportunity_viewed", { opp: o.title })} data-testid={`opp-card-${i}`}
                className="group border hairline bg-[#141217] p-6 flex flex-col hover:border-[#EF3340] transition-colors fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-label text-[10px] tracking-widest text-[#EF3340] border border-[#EF3340]/40 px-2 py-1">{o.type}</span>
                  {o.savings > 0 && <span className="font-display text-xl text-[#5FB878]">save {money(o.savings)}</span>}
                </div>
                <h3 className="font-display text-2xl leading-tight mb-2 group-hover:text-[#EF3340] transition-colors">{o.title}</h3>
                <p className="font-serif text-sm text-stone-400 leading-relaxed mb-3">{o.description}</p>
                {o.tradeoffs && <p className="font-label text-[10px] text-stone-500 mb-3">TRADEOFF · {o.tradeoffs}</p>}
                <div className="mt-auto flex items-center justify-between border-t hairline pt-3">
                  <span className="font-label text-[10px] text-stone-500">{o.source}{o.sponsored ? " · SPONSORED" : ""}</span>
                  {o.is_affiliate && <span className="font-label text-[9px] text-stone-600">AFFILIATE LINK</span>}
                  <ArrowUpRight className="w-4 h-4 text-stone-600 group-hover:text-[#EF3340] transition-colors" />
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
      <EmailCapture />
    </div>
  );
}
